import { cloneDeep } from 'lodash-es';

import {OrderItemDelivery} from '../models/order-item-delivery.model';
import {Order} from '../models/order.model';
import {User} from '../models/user.model';
import {ECOMMERCE_TYPE} from '../constants/order-types';
import {OrderItem} from '../models/order-item.model';
import {Address} from '../models/address.model';
import {allocationGroupIdOf} from '../models/order-preset-address';
import {isEmptyArray} from './utils';
import {CAROUSEL_TYPE} from '../constants/product-type';
import {ProductCatalog} from '../models/product-catalog.model';
import {HeroSlide} from '../interfaces/hero-slide';
import {ORDER_OUT_OF_STOCK} from '../constants/texts';
import {EXTRA_INTERNAL_ORDER_LENGTH, INTERNAL_ORDER_LENGTH} from '../constants/globals';
import {Features} from '../interfaces/features';
import {DistributionList} from '../models/distribution-list.model';
import {AddressType} from '../enums/address-type';
// type only: product.model already imports from here, and a value import would close the cycle
import type {Product} from '../models/product.model';
import {AbstractControl, ValidatorFn} from '@angular/forms';

const orderOutOfStock = 'SOLD_OUT';


export function computeShipmentLabel(delivery: OrderItemDelivery): string {
    if (!delivery) {
        return '';
    }
    // IMS-BTI label has higher priority
    if (delivery.isIMS) {
        return delivery.IMSAttention;
    }

    let label = delivery.labelWithoutWslrPrefix;

    const contact = delivery.mainOrFirstContactName;
    if (contact) {
        label += ` - ${contact}`;
    }

    return label;
}

export function isWSLROrder(order: Order, currentUser: User) {
    if (!order || !currentUser) {
        return false;
    }
    const result = order.entity_id > 0 &&
        order.entity_id !== currentUser.entity_id;
    return result;
}

export function isPaymentMethodDefined(order: Order, currentUser: User, canEditSap = false) {
    if (!order) {
        return false;
    }

    if (!canEditSap) {
        return true;
    }

    if (isWSLROrder(order, currentUser)) {
        return true;
    }

    if (order.type === ECOMMERCE_TYPE) {
        return order.hasCostCenter() || order.hasCreditCard();
    } else {
        return order.hasCostCenter();
    }
}


// takes neither the order item nor a delivery: what a user may ship to is a property of the order
export function computeAvailableAddresses(userAddresses: Address[], order: Order): Address[] {
    if (!order || order.isWSLR) {
        return [];
    }

    if (order.hasDistributionList) {
        return order.distributionList.addresses || [];
    }

    const allAddresses = [...userAddresses];
    return allAddresses;
}


/**
 * What this reads out of a failed request: an HttpErrorResponse, or the parsed body on its own.
 * `data` arrives in all three shapes the branches below take apart.
 */
export interface DetailedError {
    error?: {
        data?: string | string[] | { messages?: string[] };
        message?: string;
    };
}

export function findDetailedErrorMessage(error: DetailedError = {}, options: {hideResponseError?: boolean, customErrorText?: string} =  {}): string {
    if (options.hideResponseError) {
        return '';
    }

    if (options.customErrorText) {
        return options.customErrorText;
    }

    const errorData = error.error;
    if (errorData) {
        const data = errorData.data;
        if (data) {
            if (typeof data === 'string') {
                return data
            }
            if (!Array.isArray(data) && !isEmptyArray(data.messages)) {
                return data.messages.join('<br/>');
            }
            if (Array.isArray(data) && !isEmptyArray(data)) {
                return data.join(' ');
            }

        } else if (errorData.message) {
            if (errorData.message.includes(orderOutOfStock)) {
                return ORDER_OUT_OF_STOCK;
            }
            return errorData.message;
        } else {
            return 'There was a problem connecting to the server. Please reload the page and try again.';
        }
    }
    return '';
}

export function computeDiscountPrice(price: number, discount: number ): number {
    if (!price) {
        return 0;
    }

    if (!discount) {
        return price;
    }

    return price - discount;
}

export function computeProductPrice(product, quantity = 1, sku = null): number {
    if (!product) {
        return 0;
    }

    return product.getPrice(quantity, sku);
}

// export function computeDiscountProductPrice(product, quantity = 1, sku = null): number {
//     if (!product) {
//         return 0;
//     }
//     const price = computeProductPrice(product, quantity, sku);
//
//     if (!product.discount) {
//         return price;
//     }
//
//     return computeDiscountPrice(price, this.discount);
//
// }


export function computeMinQuantity(product, sku = null): number {
    if (!product) {
        return 0;
    }
    return product.getMinQuantity(sku);
}

export function computeGroupMinQuantity(product, sku = null): number {
    if (!product) {
        return 0;
    }
    return product.getGroupMinQuantity(sku);
}

export function computeMaxQuantity(product, sku = null): number {
    if (!product) {
        return 0;
    }
    return product.getMaxQuantity(sku);
}

export function computeAggregatedQuantity(product, sku = null): number {
    if (!product) {
        return 0;
    }
    return product.getAggregatedQuantity(sku);
}

export function computeGroupAggregatedQuantity(product, sku = null): number {
    if (!product) {
        return 0;
    }
    return product.getGroupAggregatedQuantity(sku);
}



export function convertCatalogsToHeroSlides(catalogs: ProductCatalog[]): HeroSlide[] {
    const result: HeroSlide[] = [];
    if (isEmptyArray(catalogs)) {
        return result;
    }
    catalogs.forEach( catalog => {
        if (catalog.featureType === CAROUSEL_TYPE) {
            const img = catalog.getHeroImage();
            if (img) {
                result.push({
                    image: img['url'],
                    headline: catalog.label,
                    description: '',
                    url: catalog.programDetailsUrl,
                });
            }
        }

    });
    return result.slice(0, 5);
}



// export function recalculateIsFirstItemInSortedAddressArray(addressList: Address[]): void {
//     let currentAddressType: number = null;
//
//     for (let i = 0; i < addressList.length; i++) {
//         const currentAddress = addressList[i];
//         if (!currentAddressType || currentAddressType !== currentAddress.address_type) {
//             currentAddressType = currentAddress.address_type;
//             addressList[i] = new Address({
//                 ...currentAddress,
//                 isWSLR: currentAddress['isWSLR'],
//                 isFirstItem: true,
//             });
//         } else {
//             addressList[i] = new Address({
//                 ...currentAddress,
//                 isWSLR: currentAddress['isWSLR'],
//                 isFirstItem: false,
//             });
//         }
//     }
// }
//
// export function getSortedWslrIds (addressesList: Address[], userEntityId: number): number[] {
//     if (isEmptyArray(addressesList)) {
//         return [];
//     }
//     let ids: number[] = [];
//
//     addressesList.forEach( a => {
//         const idList = a.wslrIds;
//
//         idList.forEach( id => {
//             const addressEntityId = id || userEntityId;
//             if (!ids.includes(addressEntityId)) {
//                 ids.push(addressEntityId);
//             }
//         })
//     });
//
//     if (ids.length > 1) {
//         if (ids.includes(userEntityId)) {
//             const restIds = ids.filter(i => i !== userEntityId).sort();
//             ids = [];
//             ids.push(userEntityId, ...restIds);
//         } else {
//             ids = ids.sort();
//         }
//     }
//     return ids;
// }

type WbsValidationOptions = {
    showInternalOrderCodes?: boolean;
    showWBS?: boolean;
    showBillingCodeDropdown?: boolean;
    billingCodeLabel?: string;
};

// returns  error message if validation failed
export function validateWbsForUnique(
    order: Order,
    orderItem: OrderItem,
    options: WbsValidationOptions = {}
): string {
    if (isEmptyArray(orderItem.deliveries)) {
        return '';
    }

    const filteredDeliveries = orderItem.deliveries.filter(delivery => delivery.quantity > 0);

    const requireInternalOrderPerShipment =
        options.showBillingCodeDropdown ||
        (options.showInternalOrderCodes && !options.showWBS);

    if (requireInternalOrderPerShipment) {
        // validate length of wbd and internal order
        const billingLabel = options.billingCodeLabel || 'Internal Order';
        for (const delivery of filteredDeliveries) {
            const effectiveInternalOrder = delivery.internalOrder || order.internalOrder;
            if (!effectiveInternalOrder) {
                if (options.showBillingCodeDropdown) {
                    return `${billingLabel} should be provided for each item`;
                }
                return `${billingLabel} should be provided for each shipment`;
            }

            if (effectiveInternalOrder.length > EXTRA_INTERNAL_ORDER_LENGTH) {
                return `${billingLabel} should not exceed ${EXTRA_INTERNAL_ORDER_LENGTH} symbols.`;
            }

            if (!options.showBillingCodeDropdown && !wbsIOSymbolsAllowed(effectiveInternalOrder)) {
                return `${billingLabel} allows only digits, letters, /, #, -, . and space`;
            }
        }

    } else if (options.showWBS) {
        const internalOrderLabel = order.isWBSDefined ? 'WBS' : 'Internal Order';
        // validate length of wbd and internal order
        for (const delivery of filteredDeliveries) {
            if (delivery.internalOrder && delivery.internalOrder !== '0') {
                if (delivery.internalOrder.length > INTERNAL_ORDER_LENGTH) {
                    return`${internalOrderLabel} should not exceed ${INTERNAL_ORDER_LENGTH} symbols.`;
                }

                if (!wbsIOSymbolsAllowed(delivery.internalOrder)) {
                    return `${internalOrderLabel} allows only digits, letters, /, #, -, . and space`;
                }
            }
        }
    }


    return validateDeliveryDuplicates(order, orderItem, options);
}

// returns error message if two deliveries of the item share an address (and billing code)
export function validateDeliveryDuplicates(
    order: Order,
    orderItem: OrderItem,
    options: WbsValidationOptions = {}
): string {
    if (isEmptyArray(orderItem.deliveries)) {
        return '';
    }

    // A BW allocated-inventory warehouse may repeat once per allocation group, so its rows are
    // unique by address + allocation group (+ IO/WBS) rather than by address.
    const warehousesKeyedByAllocationGroup = order?.isByingWindow && orderItem.product?.isEnabledAllocatedInventory;
    const deliveriesForDuplicateValidation: OrderItemDelivery[] = [];
    const warehouseDeliveriesForDuplicateValidation: OrderItemDelivery[] = [];

    for (const delivery of orderItem.deliveries) {
        if (!(delivery.quantity > 0)) {
            continue;
        }

        if (!warehousesKeyedByAllocationGroup || delivery.getAddress().isWarehouse !== true) {
            deliveriesForDuplicateValidation.push(delivery);
        } else if (delivery.allocation_group_id != null) {
            // a row without a group is reported by validateForSelectedAllocationGroup
            warehouseDeliveriesForDuplicateValidation.push(delivery);
        }
    }

    const compareInternalOrders = options.showInternalOrderCodes || options.showWBS;
    const compareWarehouseBillingCodes = compareInternalOrders || options.showBillingCodeDropdown;
    const warehouseKeys = new Set<string>();
    for (const delivery of warehouseDeliveriesForDuplicateValidation) {
        const uniqueKey = delivery.orderAddressKey
            + (compareWarehouseBillingCodes ? '_' + (delivery.internalOrder || order.internalOrder || '') : '');
        if (warehouseKeys.has(uniqueKey)) {
            return 'You cannot have the same address and allocation group';
        }
        warehouseKeys.add(uniqueKey);
    }

    // check for both flags
    if (compareInternalOrders) {
        const uniqueData: string[] = [];
        const  getInternalOrder = (delivery: OrderItemDelivery): string => {
            // if delivery WBS is no defined, then order WBS is used
            return  delivery.internalOrder || order.internalOrder;
        }

        for (const delivery of deliveriesForDuplicateValidation) {
            const uniqueKey = (delivery.uniqueAddressId ||  '') + '_' + getInternalOrder(delivery);
            if (!uniqueData.includes(uniqueKey)) {
                uniqueData.push(uniqueKey);
            } else {
                const internalOrderLabel = order.isWBSDefined ? 'WBS' : 'Internal Order';
                return `You cannot have the same address and ${internalOrderLabel} for multiple deliveries`;
            }
        }
    }


    if (!orderItem.product?.isCustomizable && !options.showInternalOrderCodes && !options.showWBS) {
        // check when no flags have been defined - restrict only when both address and billing code are the same
        const uniqueIds: string[] =  [];
        for (const delivery of deliveriesForDuplicateValidation) {
            const uniqueKey: string = (delivery.uniqueAddressId || '') + '_' + (delivery.internalOrder || order.internalOrder || '');
            if (!uniqueIds.includes(uniqueKey)) {
                uniqueIds.push(uniqueKey);
            } else {
                return `You cannot have the same address for multiple deliveries`;
            }
        }
    }

    return '';
}


export function validateWareHouseAddresses(order: Order, orderItem: OrderItem,  featureFlags: Features): string {
    if (isEmptyArray(orderItem.deliveries)) {
        return '';
    }

    const  clonedOrderItem =  cloneDeep<OrderItem>(orderItem);

    // exclude deliveries with 0 qty  and warehouse addresses
    clonedOrderItem.deliveries = orderItem.deliveries.filter( d => {
        return d.quantity > 0 && d.getAddress().isWarehouse !== true;
    });

    return validateWbsForUnique(order, clonedOrderItem, featureFlags);

}

/**
 * Whether a list view column may take this item - the single encoding of the rule, shared by the row
 * (which knows the column address) and by the cell (which knows the delivery).
 *
 * A warehouse column ships out of an allocation group, so only an item that draws on globally
 * allocated inventory belongs in it. The reverse does not hold: grid view lets such an item ship to
 * an ordinary address with no group at all - validateForSelectedAllocationGroup only demands a group
 * for warehouse deliveries, and hideBuyingWindowAllocationControl hides the group picker everywhere
 * else - so list view must not be stricter than the view next to it.
 *
 * On demand has no warehouse columns at all: its allocated inventory items pick their group per
 * cell, from the dropdown the list view column renders, so every column takes every item.
 *
 * Keyed on the column being a warehouse rather than on it carrying a group: allocation groups on
 * order rows are new, so every warehouse row saved before them carries none and would otherwise pass
 * for an ordinary column - letting a non allocated inventory item be ordered into it.
 */
export function listViewColumnAcceptsProduct(
    product: Pick<Product, 'isBuyingWindow' | 'isEnabledAllocatedInventory'>,
    isWarehouseColumn: boolean): boolean {

    if (product?.isBuyingWindow !== true) {
        return true;
    }

    const isAllocatedInventoryItem = product?.isEnabledAllocatedInventory === true;

    return isAllocatedInventoryItem || !isWarehouseColumn;
}

// returns  error message if validation failed, or empty string
export function validateForSelectedAllocationGroup(orderItem: OrderItem): string {
    if (!orderItem) {
        return '';
    }
    const product  =  orderItem.product;
    if (!product) {
        return '';
    }

    // only for allocated inventory products
    if (!product.isEnabledAllocatedInventory) {
      return '';
    }

    const errorMessage = orderItem?.id  ?
      'Please select an allocation group before clicking the Update button.' :
      'Please select an allocation group before clicking the Add to Cart button.'

    // check for BW products
    if (product.isBuyingWindow) {
        // check for each delivery
        for (const delivery of orderItem.deliveries) {
            if (delivery.quantity > 0
              && delivery.getAddress().isWarehouse
              && !delivery.allocation_group_id) {

                // allocation group is not selected
                return errorMessage;
            }
        }
    } else {
        //  check for OD  products
        const  skuLength = product.skus?.length || 0;
        if (!skuLength) {
            return '';
        }

        // remove  deliveries with 0 qty
        const filteredDeliveries = orderItem.deliveries.filter( d => d.quantity > 0);

        // each  delivery  should have  allocation group selected
        for (const delivery of filteredDeliveries) {

            const allocationGroups = delivery.allocation_groups;
            if (isEmptyArray(allocationGroups)) {
                // not  all allocation groups  are selected
                return errorMessage;
            }

            // all groups should have  selected allocation group
            for (const ag of allocationGroups) {
                if (ag.quantity > 0 && !ag.allocation_group_id) {
                    return errorMessage;
                }
            }
        }
    }


    return '';
}


/**
 * An on demand item draws its whole quantity from one allocation group, so every delivery of the
 * item has to name the same one. Grid view has enforced this since allocation groups shipped
 * (product-order-item.component.ts validateAllocationGroups); list view can reach the same state
 * now that each of its columns carries its own group picker, so it has to enforce it too.
 *
 * Buying window is untouched: there the group belongs to the warehouse column, and one item
 * legitimately ships to several warehouses in different groups.
 */
export function allocationGroupsAreConsistent(orderItem: OrderItem): boolean {
    if (!orderItem || orderItem.product?.isBuyingWindow) {
        return true;
    }

    let allocationGroupId: number; // should be the same for all deliveries

    for (const delivery of orderItem.deliveries || []) {
        for (const ag of delivery.allocation_groups || []) {
            if (!ag?.allocation_group_id) {
                continue;
            }

            if (!allocationGroupId) {
                allocationGroupId = ag.allocation_group_id;
            } else if (allocationGroupId !== ag.allocation_group_id) {
                return false;
            }
        }
    }

    return true;
}


// validate order item for 0 quantity and customizations, return error message or empty string
export function validateOrderItem(orderItem: OrderItem): string {
    const orderItemQuantity = orderItem.deliveriesTotalQuantity;
    // check for zero quantity
    if (!orderItem.id && orderItemQuantity === 0) {
        return `Please add a quantity before clicking ${orderItem.init_quantity > 0 ? 'Update' : 'Add to Cart'}`;
    }

    // check for customizations
    // if (!orderItem.id && orderItem.product.isCustomizable) {
    //
    //     if (!orderItem.isCustomizationDefined) {
    //         return 'Please customize the product';
    //     }
    // }

    const minError  = validateForMinQuantity(orderItem);
    if (minError) {
        return minError;
    }

    const  addressOrShipmentMethodError  =  orderItem.validateAddressesAndShipmentMethodsError();
    if (addressOrShipmentMethodError) {
        return addressOrShipmentMethodError;
    }

    return '';
}



export function validateForMinQuantity(orderItem: OrderItem): string {
    if (!orderItem) {
        return '';
    }
    const product  =  orderItem.product;
    if (!product) {
        return '';
    }

    // only for on-demand
    if (orderItem.isBuyingWindowOrder) {
        return ''
    }

    if ( orderItem.quantity === 0) {
        return '';
    }

    if (product.hasMinQuantity && orderItem.quantity < product.minQuantity) {
        return `This item has a minimum purchase of ${product.minQuantity}`;
    }

    return '';
}

export function validateForMaxQuantity(orderItem: OrderItem): string {
    if (!orderItem) {
        return '';
    }
    const product  =  orderItem.product;
    if (!product) {
        return '';
    }

    // only for on-demand
    if (orderItem.isBuyingWindowOrder) {
        return '';
    }

    const maxQuantity  =  product.computedMaxQuantity;
    if (maxQuantity > 0) {
        if (orderItem.quantity > maxQuantity) {
            return `This item has a maximum purchase of ${maxQuantity}`;
        }
    }
    return '';
}

export function  equalsArrayCheck  (a: number[], b: number[]): boolean {
    return a?.length === b?.length &&
    a.every((v, i) => v === b[i]);
}


/**
 * Identifies an address inside an order. A warehouse can be present several times - once per
 * allocation group - so the group belongs to the key; the other address types never carry one,
 * which keeps the key equal to uniqueAddressId for them.
 */
export function orderAddressKey(address: Address): string {
    if (!address) {
        return '';
    }

    const allocationGroupId = allocationGroupIdOf(address);

    return allocationGroupId != null
        ? `${address.uniqueAddressId}-${allocationGroupId}`
        : address.uniqueAddressId;
}

/**
 * The set of columns an address list stands for, as one comparable value. A string rather than the
 * list itself: findAllOrderAddresses hands back the order's own preset entries, so a caller holding
 * that array before a dialog runs would be holding whatever the dialog made of them afterwards.
 * Reading the keys up front takes the answer out of reach of that.
 *
 * Order does not count - the same rows in another sequence are the same columns - and the key
 * already tells two rows of one warehouse apart by their allocation group.
 *
 * ProductsListComponent, ProductListItemComponent and OrderService each still carry their own copy
 * of this comparison - worth folding into this one, but not while the address-change path is in flux.
 */
export function orderAddressRowsKey(addresses: Address[]): string {
    return (addresses || [])
        .filter(address => address != null)
        .map(address => orderAddressKey(address))
        .sort()
        .join(',');
}

/**
 * The delivery that fills a given column of an order item, or undefined.
 *
 * Compared field by field rather than through orderAddressKey: OrderItemDelivery.uniqueAddressId is
 * null for addr_id 0 - an imported address - while Address.uniqueAddressId is not, so the two keys
 * never meet for such a row and its money would vanish out of the column.
 */
export function findDeliveryForAddress(deliveries: OrderItemDelivery[], address: Address): OrderItemDelivery {
    if (isEmptyArray(deliveries) || !address) {
        return undefined;
    }

    const isSameAddress = (delivery: OrderItemDelivery): boolean =>
        delivery.addr_id === address.id && delivery.address_type === address.address_type;

    const allocationGroupId = allocationGroupIdOf(address) ?? null;
    const delivery = deliveries.find(d => isSameAddress(d) && (d.allocation_group_id ?? null) === allocationGroupId);

    if (delivery || allocationGroupId === null) {
        return delivery;
    }

    // Allocation groups on order rows are new, so a warehouse delivery saved before them carries none
    // and still belongs to this warehouse - the same fallback the cells of the row make.
    return deliveries.find(d => isSameAddress(d) && d.allocation_group_id == null);
}

export function areAddressesEqual(a: Address[], b: Address[]): boolean {
    if (a.length !== b.length) {
        return false;
    }

    const uniqueIdListA  =  a.map(adr => orderAddressKey(adr)).sort();
    const uniqueIdListB  =  b.map(adr => orderAddressKey(adr)).sort();

    return uniqueIdListA.join(',') === uniqueIdListB.join(',');
}

export interface AddressUpdateResult  {address: Address, status: '' | 'unchanged' | 'added' | 'deleted' | 'tested'}

export function findModifiedAddress(initialAddressList: Address[], updatedAddressList: Address[]): AddressUpdateResult[] {
    // Filter out null/undefined placeholder entries before any processing
    const filteredInitial = (initialAddressList || []).filter((a): a is Address => a != null);
    const filteredUpdated = (updatedAddressList || []).filter((a): a is Address => a != null);

    if (!filteredInitial.length) {
        if (!filteredUpdated.length) {
            return [];
        }
        return filteredUpdated.map(item => ({ address: item, status: 'added' }));
    }

    if (!filteredUpdated.length) {
        // Empty updated list means all addresses were deleted
        return filteredInitial.map(item => ({ address: item, status: 'deleted' }));
    }

    // Keyed by the order address key: two rows of the same warehouse differ by their allocation group,
    // so they stay separate entries here instead of collapsing into one.
    const getUniqueKey = (address: Address): string => {
        return orderAddressKey(address);
    };

    // Build lookup maps for fast comparison
    const initialMap = new Map(filteredInitial.map(addr => [getUniqueKey(addr), addr]));
    const updatedMap = new Map(filteredUpdated.map(addr => [getUniqueKey(addr), addr]));

    const results: AddressUpdateResult[] = [];

    // Detect unchanged and new addresses
    filteredUpdated.forEach(updatedAddr => {
        const key = getUniqueKey(updatedAddr);
        if (initialMap.has(key)) {
            results.push({ address: updatedAddr, status: 'unchanged' });
        } else {
            // A row that is not in the initial list is new, whatever sits at its position there: the
            // two lists are not aligned - the initial one holds the rows that carry quantity, the
            // updated one every row of the dialog - so pairing them by index reaches an address the
            // row has nothing to do with.
            results.push({ address: updatedAddr, status: 'added' });
        }
    });

    // Detect deleted addresses
    filteredInitial.forEach((initialAddr) => {
        const key = getUniqueKey(initialAddr);
        if (!updatedMap.has(key)) {
            results.push({ address: initialAddr, status: 'deleted' });
        }
    });

    return results;
}

// subtitute  product slug  in the path with another product slug
export function replaceCurrentPathWithProductSlug(currentPath: string, productSlug: string): string {
    if (!currentPath || !productSlug) {
        return '';
    }

    const paths  = currentPath.split('/programs/');
    if (paths.length !== 2) {
        //  no programs  in the path
        return '';
    }

    const  restPaths = paths[1].split('/');
    if (restPaths.length === 0) {
        // path doesn't  contain  product slug
        return `${paths[0]}/programs/${productSlug}`;
    }

    return `${paths[0]}/programs/${restPaths[0]}/${productSlug}`;
}

export function sortDistributionLists(distributionLists: DistributionList[]): DistributionList[] {
    if (isEmptyArray(distributionLists)) {
        return [];
    }

    return distributionLists.sort((a, b) => {
        return a.label.localeCompare(b.label);
    });
}


export function sortAddresses(addressList: Address[]): Address[] {
    if (isEmptyArray(addressList)) {
        return [];
    }

    // sort by label
    const sortAddressListByLabel = (addresses: Address[]): Address[] => {
        if (isEmptyArray(addresses)) {
            return [];
        }


        const compareByLabels = (address1: Address, address2: Address): number => {
            return (address1.label?.toLowerCase() || '').localeCompare(address2.label?.toLowerCase() || '');
        }


        return addresses.sort((a, b) => {

            // Sort by active (true comes first)
            const activeDiff = Number(b.active) - Number(a.active);
            if (activeDiff !== 0) {
                return activeDiff;
            }

            // Sort by default (true comes first)
            const defaultDiff = Number(b.isDefault) - Number(a.isDefault);
            if (defaultDiff !== 0) {
                return defaultDiff;
            }

            // Sort by favorite (true comes first)
            const favoriteDiff = Number(b.isFavorite || false) - Number(a.isFavorite || false);
            if (favoriteDiff !== 0) {
                return favoriteDiff;
            }

            // Sort alphabetically by label
            return compareByLabels(a, b);

        });
    }


    // mark first item in the list
    const markFirstItem = (addresses: Address[]): Address[] => {
        if (isEmptyArray(addresses)) {
            return [];
        }
        return addresses.map( (address, index) => {
            return new Address({...address, isFirstItem: index === 0});
        })
    }


    let corporateAddresses =  sortAddressListByLabel(addressList.filter((address) => address.isCorporate));
    corporateAddresses = markFirstItem(corporateAddresses);

    let personalAddresses  =  sortAddressListByLabel(addressList.filter((address) => address.isPersonal));
    personalAddresses = markFirstItem(personalAddresses);

    let warehouseAddresses = sortAddressListByLabel(addressList.filter((address) => address.isWarehouse));
    warehouseAddresses = markFirstItem(warehouseAddresses);

    // corporate  addresses  are always  first
    return  [
        ...corporateAddresses,
        ...personalAddresses,
        ...warehouseAddresses,
    ];
}

// in some cases order item  creates when order doesn't  exist,
// at this moment  it's not clear whether WBS or IO is used, and it should be checked at saving api
export function checkIOCodeAgainstOrder(orderItem: OrderItem, order: Order) {
  // only for new created order items
  if (orderItem.id || isEmptyArray(orderItem.deliveries)) {
    return;
  }

  if (!order) {
    return;
  }

  if (order.wbsCode) {
    orderItem.deliveries.forEach( d => {
      if (d.internal_order) {
        d.wbs = d.internal_order;
        d.internal_order = '';
      }
    });
  } else if (order.internalOrderCode) {
    orderItem.deliveries.forEach(d => {
      if (d.wbs) {
        d.internal_order = d.wbs;
        d.wbs = '';
      }
    });
  }
}

const WBS_REGEX = /^[A-Za-z0-9/\-.# ]*$/;
// WBS/IO code allows only digits, letters, hyphen, dash, dot, space and slash
export function  wbsIOSymbolsAllowed(val: string): boolean {
    if (!val) {
        return true; // skip  validation  for empy
    }
    return WBS_REGEX.test(val);
}


// Order text allows letters (A-Z a-z), digits (0-9), space ( ), comma (,), hyphen (-), quote ('), dot (.), hash (#)
export const ORDER_TEXT_REGEX = /^[A-Za-z0-9 \-',.#]*$/;

export function isValidOrderText(value: string | null | undefined): boolean {
    if (!value) {
        return true;
    }
    return ORDER_TEXT_REGEX.test(value);
}

export function getInvalidOrderTextMessage(fieldName: string): string {
    return `${fieldName} allows only letters, digits, space, comma, dot, -, ' and #`;
}

export function forbiddenOrderCharacters(): ValidatorFn {
    return (control: AbstractControl) => {
        if (isValidOrderText(control.value)) {
            return null;
        }
        return { forbiddenCharacters: true };
    };
}

export function getDistributionOrPresetAddresses(distrbutionList: DistributionList, presetAdresses: Address[]): Address[] {
    if (distrbutionList && distrbutionList.hasAddresses) {
        return [...distrbutionList.addresses, ...(presetAdresses || [])];
    }

    return presetAdresses || [];
}

export function setupDistributionListDeliveries(order: Order, orderItem: OrderItem, createMissingPresetsDeliveries = true) {
    if (!order || !orderItem || !orderItem.product) {
        return;
    }

    // Rows the user explicitly removed must not be recreated from the preset/DL addresses,
    // which are only pruned once the save succeeds (AB#86452).
    const removedAddressIds = orderItem.removedPresetAddressIds || [];
    const addresses = order.distributionOrPresetAddresses
        .filter(address => !removedAddressIds.includes(address.uniqueAddressId));

    if (isEmptyArray(addresses)) {
        return;
    }

    if (orderItem.product.hasVariations) {
        return;
    }

    // AB#86500 - a warehouse in the order settings is a desired value rather than a shipping row of its
    // own: it is the pair (warehouse, allocation group), and it applies to a product that carries
    // allocated inventory. For such a product the warehouse is where the order ships, so the row is a
    // delivery like any other and the ship-to address is filled in from it - matched by the pair rather
    // than by the address, since two rows of one warehouse differ only by their group
    // (findDeliveryForAddress also carries the fallback for a delivery saved before groups existed).
    // A product without allocated inventory has nothing to do with that pairing, so the warehouse
    // preset is ignored for it: no row of its own, and the row seeded for it by getItemForProduct goes.
    const warehousesApply = orderItem.product.isEnabledAllocatedInventory === true;
    const isWarehouseDelivery = (delivery: OrderItemDelivery): boolean =>
        delivery?.address_type === AddressType.Warehouse;

    // a warehouse the cart already ships to stays either way: dropping it would take away products the
    // user has ordered, which is not this function's to do
    const existingDeliveries = warehousesApply
        ? [...orderItem.deliveries]
        : orderItem.deliveries.filter(delivery => !isWarehouseDelivery(delivery) || delivery.quantity > 0);
    const sourceAddresses = warehousesApply
        ? [...addresses]
        : addresses.filter(address => !address?.isWarehouse);

    if (isEmptyArray(sourceAddresses)) {
        // every location of the order is a warehouse this product ignores: there is no row to build
        // from the presets, and the empty row the Product page adds so the user can pick an address is
        // all the item has - it is not this function's to take away either.
        orderItem.deliveries = existingDeliveries;
        return;
    }

    // per source address, the delivery it was matched to - consumed as they are matched, so two rows
    // of one warehouse cannot both claim the single delivery saved without an allocation group
    const matchedDeliveries: OrderItemDelivery[] = new Array(sourceAddresses.length);
    let unmatchedPresetCount = 0;

    sourceAddresses.forEach((address, index) => {
        const existingDelivery = findDeliveryForAddress(existingDeliveries, address);
        if (existingDelivery) {
            // this address already exists in the deliveries
            matchedDeliveries[index] = existingDelivery;
            // remove from existing deliveries
            existingDeliveries.splice(existingDeliveries.indexOf(existingDelivery), 1);
        } else {
            unmatchedPresetCount++;
        }
    });

    const updatedDeliveries = matchedDeliveries.filter(delivery => !!delivery);

    // AB#84340 reads a delivery that no source address backs as a preset the user replaced. A warehouse
    // row this product ignores is no replacement - its preset sits right there in the order settings,
    // it just does not apply here - so it must not suppress the rows the other presets are entitled to.
    const warehouseBackedDeliveries = warehousesApply ? new Set<OrderItemDelivery>() : new Set(
        addresses.filter(address => address?.isWarehouse)
            .map(address => findDeliveryForAddress(existingDeliveries, address))
            .filter(delivery => !!delivery));

    // Remaining deliveries are those that don't match any preset/DL address.
    // If none of the source addresses matched and there are active remaining deliveries,
    // the user must have replaced the preset address — don't recreate phantom rows (AB#84340).
    const activeRemainingDeliveries = existingDeliveries.filter(
        d => d.quantity > 0 && !warehouseBackedDeliveries.has(d));
    const matchedAny = unmatchedPresetCount < sourceAddresses.length;
    const shouldCreateMissing = createMissingPresetsDeliveries
        && (activeRemainingDeliveries.length === 0 || matchedAny);

    // Second pass: create empty deliveries for unmatched presets (in order)
    if (shouldCreateMissing && unmatchedPresetCount > 0) {
        // Rebuild with placeholders inserted at correct positions
        updatedDeliveries.length = 0;
        sourceAddresses.forEach((address, index) => {
            updatedDeliveries.push(matchedDeliveries[index] || createEmptyDeliveryForAddress(address));
        });
    }

    // add remaining deliveries if any
    if (!isEmptyArray(existingDeliveries)) {
        const deliveriesToKeep = existingDeliveries.filter(d => d.quantity > 0 || !d.id);
        updatedDeliveries.push(...deliveriesToKeep);
    }

    orderItem.deliveries = updatedDeliveries;
}

function createEmptyDeliveryForAddress(address: Address): OrderItemDelivery  {
        const delivery = new OrderItemDelivery({
            type: 'SHIPPING',
            method: '',
            quantity: 0,
            data: {}
        });

        delivery.setAddress(address, null, {canOrder: true, isEmployee: true});
        return delivery;
}

export function formatCountdown(val: number): string {
    if (!val) {
        return '';
    }

    // get minutes and seconds
    const minutes = Math.floor(val / 60);
    const seconds = val % 60;


    const suffixSeconds = seconds > 1 ? 'seconds' : 'second';
    if (minutes === 0) {
        return `${seconds} ${suffixSeconds}`;
    }

    const suffixMinutes = minutes > 1 ? 'minutes' : 'minute';
    return  seconds !== 0 ?
        `${minutes} ${suffixMinutes} ${seconds} ${suffixSeconds}` : `${minutes} ${suffixMinutes}`;
}
export function concatAddressesForDistributionLists(list: DistributionList[]): Address[] {
    const result: Address[] = [];
    if (isEmptyArray(list)) {
        return result;
    }

    for (const dl of list) {
        if (dl.hasAddresses) {
            dl.addresses.forEach( a => {
                if (!result.find( i => i.uniqueAddressId === a.uniqueAddressId)) {
                    result.push(a);
                }
            })
        }

    }

    return result;
}

export function canShowOrderBudgetInfo(order: Order) {
    if (!order) {
        return false;
    }

    // show budget applied  when  order has budgets and when budget used flag is true,
    // because for some orders with budgets the flag can be false, but we still want to show applied budget info

    // temporary hide this  approach and  show  only if budget > 0
    // return !isEmptyArray(order?.budgets) || order.budget_used === true;
    return order?.budgetTotal > 0;
}

export function canShowOrderBudgetDetails(order: Order) {
    if (!order) {
        return false;
    }

    // show budget details only when budgets in order exist
    // temporary hide this  approach and  show  only if budget > 0
    // return !isEmptyArray(order?.budgets);
    return order?.budgetTotal > 0;
}


export function computeBudgetAppliedAmount(order: Order): number {
    if (!order) {
        return 0;
    }
    // temporary hide this  approach and  compute applied budget amount based on budget total,
    // if (!isEmptyArray(order?.budgets)) {
    //     return order.budgetTotal;
    // } else if (order.budget_used) {
    //     return  0;
    // }

    return order.budgetTotal;
}
